import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ScanResultHandler = ({ 
  scanResult = null,
  receiptData = null,
  userName = null,
  userId = null,
  isLoading = false,
  error = null,
  onRetry = () => {},
  onReset = () => {}
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (scanResult && !error && !isLoading) {
      // Auto-navigate after successful scan
      const timer = setTimeout(() => {
        navigate('/bill-overview', { 
          state: { 
            sessionId: scanResult,
            receiptData: receiptData,
            userName: userName,
            userId: userId
          }
        });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [scanResult, error, isLoading, navigate, receiptData, userName]);

  // Loading state
  if (isLoading) {
    return (
      <div className="w-full max-w-md mx-auto bg-surface border border-border rounded-lg p-6 text-center">
        <div className="animate-pulse-soft mb-4">
          <Icon name="Sparkles" size={48} className="text-primary mx-auto" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Analizando ticket con IA...
        </h3>
        <p className="text-text-secondary text-sm">
          Extrayendo conceptos e importes del ticket
        </p>
        <div className="mt-4 flex justify-center">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (scanResult && !error) {
    return (
      <div className="w-full max-w-2xl mx-auto space-y-4">
        <div className="bg-surface border border-success rounded-lg p-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
            <Icon name="CheckCircle" size={32} className="text-success" />
          </div>
          
          <h3 className="text-lg font-semibold text-foreground mb-2">
            ¡Ticket analizado correctamente!
          </h3>
          
          <p className="text-text-secondary text-sm mb-4">
            Se han extraído todos los conceptos del ticket
          </p>

          <div className="bg-muted/50 rounded-lg p-3 mb-4">
            <div className="text-xs text-text-secondary mb-1">ID de sesión:</div>
            <div className="font-mono text-sm text-foreground">{scanResult}</div>
          </div>
        </div>

        {/* Receipt preview */}
        {receiptData && (
          <div className="bg-surface border border-border rounded-lg p-6">
            <h4 className="font-semibold text-foreground mb-3 flex items-center">
              <Icon name="Receipt" size={20} className="mr-2" />
              Resumen del ticket
            </h4>
            
            {receiptData.restaurant && (
              <div className="mb-3 pb-3 border-b border-border">
                <p className="text-sm font-medium text-foreground">
                  {receiptData.restaurant}
                </p>
                {receiptData.date && (
                  <p className="text-xs text-text-secondary">
                    {new Date(receiptData.date).toLocaleString('es-ES')}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2 mb-3">
              <p className="text-xs text-text-secondary font-medium">
                Conceptos detectados: {receiptData.items?.length || 0}
              </p>
              {receiptData.items?.slice(0, 3).map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-foreground">
                    {item.quantity > 1 && `${item.quantity}x `}
                    {item.name}
                  </span>
                  <span className="font-medium text-foreground">
                    {item.totalPrice?.toFixed(2)}€
                  </span>
                </div>
              ))}
              {receiptData.items?.length > 3 && (
                <p className="text-xs text-text-secondary italic">
                  + {receiptData.items.length - 3} conceptos más...
                </p>
              )}
            </div>

            {receiptData.total && (
              <div className="pt-3 border-t border-border flex justify-between">
                <span className="font-semibold text-foreground">Total:</span>
                <span className="font-bold text-lg text-primary">
                  {receiptData.total.toFixed(2)}€
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-center space-x-2 text-sm text-text-secondary">
          <Icon name="Clock" size={16} />
          <span>Redirigiendo en unos segundos...</span>
        </div>

        <Button
          variant="default"
          fullWidth
          onClick={() => navigate('/bill-overview', { 
            state: { 
              sessionId: scanResult,
              receiptData: receiptData,
              userName: userName,
              userId: userId
            }
          })}
        >
          <Icon name="ArrowRight" size={20} className="mr-2" />
          Continuar ahora
        </Button>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full max-w-md mx-auto bg-surface border border-error rounded-lg p-6 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-error/10 rounded-full mb-4">
          <Icon name="AlertCircle" size={32} className="text-error" />
        </div>
        
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Error al analizar el ticket
        </h3>
        
        <p className="text-text-secondary text-sm mb-4">
          {error}
        </p>

        <div className="space-y-3">
          <Button
            variant="default"
            fullWidth
            onClick={onRetry}
          >
            <Icon name="RotateCcw" size={20} className="mr-2" />
            Intentar de nuevo
          </Button>

          <Button
            variant="outline"
            fullWidth
            onClick={onReset}
          >
            <Icon name="Camera" size={20} className="mr-2" />
            Tomar otra foto
          </Button>
        </div>

        {/* Common Error Solutions */}
        <div className="mt-6 pt-4 border-t border-border text-left">
          <h4 className="font-medium text-foreground text-sm mb-2">
            Consejos para mejorar el escaneo:
          </h4>
          <ul className="text-xs text-text-secondary space-y-1">
            <li>• Asegúrate de que el ticket esté completamente visible</li>
            <li>• Verifica que haya buena iluminación</li>
            <li>• El texto debe estar nítido y legible</li>
            <li>• Evita sombras y reflejos en el papel</li>
          </ul>
        </div>
      </div>
    );
  }

  return null;
};

export default ScanResultHandler;